import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowLeft, Bed, Bath, Maximize, MapPin, Check, Youtube, Trees } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '../context/LanguageContext';
import { getStaticVilla, getLiveVilla, submitContact, getImageUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { StatusBadge } from '../components/VillaCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Helper to get localized path for back button
const getLocalizedPath = (path, lang) => {
  if (lang === 'en') return path;
  return `/${lang}${path}`;
};

const VillaPage = () => {
  const { id, lang } = useParams();
  const { t, language } = useLanguage();
  const [villa, setVilla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const liveDataFetched = useRef(false);

  // Image categories
  const IMAGE_CATEGORIES = [
    { id: 'exterior', icon: '🏠' },
    { id: 'interior', icon: '🛋️' },
    { id: 'floor_plans', icon: '📐' },
    { id: 'details', icon: '✨' },
  ];
  
  // Get category label based on language
  const getCategoryLabel = (categoryId) => {
    const labels = {
      exterior: { en: 'Exterior', de: 'Außenbereich', pl: 'Zewnętrzne', sv: 'Exteriör', cs: 'Exteriér' },
      interior: { en: 'Interior', de: 'Innenbereich', pl: 'Wnętrze', sv: 'Interiör', cs: 'Interiér' },
      floor_plans: { en: 'Floor Plans', de: 'Grundrisse', pl: 'Plany pięter', sv: 'Planritningar', cs: 'Půdorysy' },
      details: { en: 'Details', de: 'Details', pl: 'Szczegóły', sv: 'Detaljer', cs: 'Detaily' },
    };
    return labels[categoryId]?.[language] || labels[categoryId]?.en || categoryId;
  };

  useEffect(() => {
    liveDataFetched.current = false;
    loadVilla();
  }, [id]);

  // Handle scroll for sticky header
  useEffect(() => {
    const handleScroll = () => {
      // Show sticky header after scrolling past 200px
      setShowStickyHeader(window.scrollY > 200);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // SWR Pattern: Static first, then live API in background
  const loadVilla = async () => {
    setLoading(true);
    
    // Step 1: Load static data immediately
    const staticData = await getStaticVilla(id);
    if (staticData) {
      setVilla(staticData);
      setLoading(false);
    }
    
    // Step 2: Fetch live data in background (only once per villa)
    if (!liveDataFetched.current) {
      liveDataFetched.current = true;
      const liveData = await getLiveVilla(id);
      if (liveData) {
        // Only update if data is different
        setVilla(prevVilla => {
          const isDifferent = JSON.stringify(prevVilla) !== JSON.stringify(liveData);
          return isDifferent ? liveData : prevVilla;
        });
      }
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitContact({
        ...formData,
        villa_id: id,
        language,
      });
      toast.success(t('contact.success'), { description: t('contact.successDesc') });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Error submitting form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!villa) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-text-secondary mb-4">{language === 'ru' ? 'Вилла не найдена' : 'Villa not found'}</p>
        <Link to={getLocalizedPath('/catalog', language)} className="text-gold hover:underline">
          {t('villa.back')}
        </Link>
      </div>
    );
  }

  const title = villa[`title_${language}`] || villa.title_en;
  const description = villa[`description_${language}`] || villa.description_en;
  const location = villa[`location_${language}`] || villa.location_en;
  const features = villa[`features_${language}`] || villa.features_en;

  const formatPrice = (price) => {
    if (villa.price_on_request) {
      const priceLabels = {
        en: 'Price on request',
        de: 'Preis auf Anfrage',
        pl: 'Cena na zapytanie',
        sv: 'Pris på förfrågan',
        cs: 'Cena na vyžádání',
      };
      return priceLabels[language] || priceLabels.en;
    }
    return new Intl.NumberFormat(language === 'de' ? 'de-DE' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Get sorted images with URLs, grouped by category
  const getImagesByCategory = (category) => {
    if (villa.images && villa.images.length > 0) {
      const filtered = villa.images.filter(img => (img.category || 'exterior') === category);
      const sorted = [...filtered].sort((a, b) => (a.order || 0) - (b.order || 0));
      return sorted.map(img => ({
        url: getImageUrl(img, 'full') || `${API_URL}${img.url}`,
        thumbUrl: getImageUrl(img, 'thumb') || `${API_URL}${img.url}`,
        id: img.id
      }));
    }
    return [];
  };

  // Get all images flat for lightbox
  const getAllImagesFlat = () => {
    const all = [];
    IMAGE_CATEGORIES.forEach(cat => {
      const catImages = getImagesByCategory(cat.id);
      catImages.forEach(img => {
        all.push({ ...img, category: cat.id });
      });
    });
    return all;
  };

  // Open lightbox
  const openLightbox = (category, index) => {
    const allImages = getAllImagesFlat();
    const catImages = getImagesByCategory(category);
    
    // Find global index
    let globalIndex = 0;
    for (const cat of IMAGE_CATEGORIES) {
      if (cat.id === category) {
        globalIndex += index;
        break;
      }
      globalIndex += getImagesByCategory(cat.id).length;
    }
    
    setLightboxImages(allImages);
    setLightboxIndex(globalIndex);
    setLightboxOpen(true);
  };

  // Get main image (first exterior or first available)
  const getMainImage = () => {
    const exteriorImages = getImagesByCategory('exterior');
    if (exteriorImages.length > 0) return exteriorImages[0].url;
    
    for (const cat of IMAGE_CATEGORIES) {
      const images = getImagesByCategory(cat.id);
      if (images.length > 0) return images[0].url;
    }
    return 'https://images.unsplash.com/photo-1757264119016-7e6b568b810d?w=1200';
  };
  
  // Count images per category
  const categoryCounts = IMAGE_CATEGORIES.reduce((acc, cat) => {
    acc[cat.id] = getImagesByCategory(cat.id).length;
    return acc;
  }, {});
  
  // Get categories that have images
  const availableCategories = IMAGE_CATEGORIES.filter(cat => categoryCounts[cat.id] > 0);

  // Convert YouTube watch URL to embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes('/embed/')) return url;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    return url;
  };

  const youtubeEmbedUrl = getYouTubeEmbedUrl(villa.youtube_url);
  
  // Generate hreflang URLs for all languages
  const languages = ['en', 'de', 'pl', 'cs', 'sv'];
  const currentUrl = `https://villa.ooo/villa/${villa.slug || villa.id}`;

  return (
    <>
      <Helmet>
        <title>{`${title} — Villa.ooo`}</title>
        <meta name="description" content={String(description).substring(0, 160)} />
        <link rel="canonical" href={currentUrl} />
        {/* hreflang tags for multilingual SEO */}
        {languages.map(lang => (
          <link key={lang} rel="alternate" hrefLang={lang} href={`${currentUrl}?lang=${lang}`} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={currentUrl} />
        {/* Open Graph */}
        <meta property="og:title" content={`${title} — Luxury Villa in Croatia`} />
        <meta property="og:description" content={String(description).substring(0, 160)} />
        <meta property="og:type" content="realestate.property" />
        <meta property="og:url" content={currentUrl} />
        {villa.main_image?.url && <meta property="og:image" content={villa.main_image.url} />}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateListing",
            "name": title,
            "description": description,
            "price": villa.price,
            "priceCurrency": villa.currency,
            "numberOfBedrooms": villa.bedrooms,
            "numberOfBathroomsTotal": villa.bathrooms,
            "floorSize": { "@type": "QuantitativeValue", "value": villa.area, "unitCode": "MTK" },
            "address": { "@type": "PostalAddress", "addressLocality": location, "addressCountry": "HR" },
            "geo": { "@type": "GeoCoordinates", "latitude": villa.latitude, "longitude": villa.longitude },
            "url": currentUrl,
            "image": villa.main_image?.url || getMainImage(),
          })}
        </script>
      </Helmet>

      {/* Compact Sticky Header - shows on scroll */}
      <div className={`fixed top-[128px] md:top-[160px] left-0 right-0 z-40 bg-bone/95 backdrop-blur-sm border-b border-gray-200 shadow-lg transition-all duration-300 ${
        showStickyHeader ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'
      }`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-2 md:py-3">
          <div className="flex items-center justify-between gap-4">
            <Link
              to={getLocalizedPath('/catalog', language)}
              className="text-text-secondary hover:text-gold transition-colors shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex-1 text-center min-w-0">
              <p className="font-heading text-sm md:text-base text-night truncate">{title}</p>
              <p className="text-xs text-text-secondary flex items-center justify-center gap-1">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{location}</span>
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-2">
              {villa.status && <StatusBadge status={villa.status} size="small" />}
              <span className="text-gold font-semibold text-sm md:text-base">{formatPrice(villa.price)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Header Bar with Title, Location, and Price */}
      <div className="bg-white border-b border-gray-100 pt-24 md:pt-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 md:py-6">
          {/* Back Button Row */}
          <Link
            to={getLocalizedPath('/catalog', language)}
            className="inline-flex items-center gap-2 text-text-secondary hover:text-gold transition-colors text-sm mb-4"
            data-testid="back-to-catalog"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('villa.back')}
          </Link>
          
          {/* Title Row */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-night leading-tight" data-testid="villa-title-sticky">
                {title}
              </h1>
              <p className="text-sm md:text-base text-text-secondary flex items-center gap-1 mt-2">
                <MapPin className="w-4 h-4 text-gold" />
                {location}
              </p>
              {/* Key Stats */}
              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-text-secondary">
                {villa.bedrooms && (
                  <span className="flex items-center gap-1">
                    <Bed className="w-4 h-4" />
                    {villa.bedrooms} {t('villa.bedrooms')}
                  </span>
                )}
                {villa.bathrooms && (
                  <span className="flex items-center gap-1">
                    <Bath className="w-4 h-4" />
                    {villa.bathrooms} {t('villa.bathrooms')}
                  </span>
                )}
                {villa.area && (
                  <span className="flex items-center gap-1">
                    <Maximize className="w-4 h-4" />
                    {villa.area} m²
                  </span>
                )}
                {villa.land_area && (
                  <span className="flex items-center gap-1 text-gold font-medium">
                    <Trees className="w-4 h-4" />
                    {t('villa.land')}: {villa.land_area} m²
                  </span>
                )}
              </div>
            </div>
            
            {/* Price & Status */}
            <div className="flex items-center gap-4 md:text-right">
              {villa.status && (
                <StatusBadge status={villa.status} size="normal" />
              )}
              <p className="text-gold font-heading text-xl md:text-2xl lg:text-3xl whitespace-nowrap" data-testid="villa-price-sticky">
                {formatPrice(villa.price)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Hero Image */}
      <section className="py-4" data-testid="villa-gallery">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden cursor-pointer group"
               onClick={() => openLightbox('exterior', 0)}>
            <img
              src={getMainImage()}
              alt={`${title} - Main Photo - Luxury Seafront Villa in Croatia`}
              title={title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="eager"
              fetchpriority="high"
            />
            {/* View all photos */}
            <div className="absolute bottom-4 right-4 bg-black/70 text-white px-4 py-2 text-sm flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <span>{language === 'ru' ? 'Смотреть все фото' : 'View all photos'}</span>
              <span className="text-gold">({villa.images?.length || 0})</span>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Categories Sections */}
      {availableCategories.length > 0 && (
        <section className="py-8 bg-bone" data-testid="villa-photo-sections">
          <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-8">
            {availableCategories.map((cat) => {
              const catImages = getImagesByCategory(cat.id);
              if (catImages.length === 0) return null;
              
              return (
                <div key={cat.id} data-testid={`photo-section-${cat.id}`}>
                  {/* Category Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xl">{cat.icon}</span>
                    <h3 className="font-heading text-lg text-night">
                      {getCategoryLabel(cat.id)}
                    </h3>
                    <span className="text-text-secondary text-sm">({catImages.length})</span>
                  </div>
                  
                  {/* Horizontal Scroll Images */}
                  <div className="overflow-x-auto pb-4 -mx-6 px-6 md:-mx-12 md:px-12 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                    <div className="flex gap-3" style={{ width: 'max-content' }}>
                      {catImages.map((img, index) => {
                        const seoAlt = `${title} - ${getCategoryLabel(cat.id)} - Photo ${index + 1} of ${catImages.length}`;
                        
                        // Different sizes for floor plans vs regular photos
                        const imageSize = cat.id === 'floor_plans' 
                          ? 'w-64 md:w-80 aspect-[4/3]' 
                          : 'w-40 md:w-48 aspect-square';
                        
                        return (
                          <button
                            key={img.id || index}
                            onClick={() => openLightbox(cat.id, index)}
                            className={`flex-shrink-0 overflow-hidden group cursor-pointer ${imageSize}`}
                          >
                            <img
                              src={img.thumbUrl || img.url}
                              alt={seoAlt}
                              title={seoAlt}
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                              loading="lazy"
                            />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          onTouchStart={(e) => {
            const touch = e.touches[0];
            e.currentTarget.dataset.touchStartX = touch.clientX;
            e.currentTarget.dataset.touchStartY = touch.clientY;
          }}
          onTouchEnd={(e) => {
            const startX = parseFloat(e.currentTarget.dataset.touchStartX || 0);
            const startY = parseFloat(e.currentTarget.dataset.touchStartY || 0);
            const endX = e.changedTouches[0].clientX;
            const endY = e.changedTouches[0].clientY;
            const diffX = endX - startX;
            const diffY = endY - startY;
            
            // Only swipe if horizontal movement is greater than vertical
            if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
              if (diffX > 0) {
                // Swipe right - previous image
                setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
              } else {
                // Swipe left - next image
                setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
              }
            }
          }}
        >
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 text-white/70 hover:text-white z-10 p-2"
            onClick={() => setLightboxOpen(false)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Navigation - hide on mobile */}
          {lightboxImages.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 hidden md:block"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
                }}
              >
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 hidden md:block"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
                }}
              >
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
          
          {/* Main Image */}
          <img
            src={lightboxImages[lightboxIndex]?.url}
            alt={`${title} - Photo ${lightboxIndex + 1} of ${lightboxImages.length}`}
            className="max-w-[90vw] max-h-[85vh] object-contain select-none"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />
          
          {/* Counter + swipe hint on mobile */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm text-center">
            <span>{lightboxIndex + 1} / {lightboxImages.length}</span>
            <span className="block md:hidden text-xs mt-1 opacity-70">← Swipe to navigate →</span>
          </div>
        </div>
      )}

      {/* Details */}
      <section className="py-12 md:py-20" data-testid="villa-details">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-10">
              {/* Description */}
              <div>
                <p className="text-text-secondary leading-relaxed whitespace-pre-line">{description}</p>
              </div>

              {/* Features */}
              {features?.length > 0 && (
                <div>
                  <h3 className="font-heading text-2xl text-night mb-6">{t('villa.features')}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-gold" />
                        <span className="text-text-secondary">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Map */}
              <div>
                <h3 className="font-heading text-2xl text-night mb-6">{t('villa.location')}</h3>
                <div className="map-container" data-testid="villa-map">
                  <MapContainer
                    center={[villa.latitude, villa.longitude]}
                    zoom={14}
                    scrollWheelZoom={false}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[villa.latitude, villa.longitude]}>
                      <Popup>{title}</Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>

              {/* YouTube Video */}
              {youtubeEmbedUrl && (
                <div data-testid="villa-video">
                  <h3 className="font-heading text-2xl text-night mb-6 flex items-center gap-2">
                    <Youtube className="w-6 h-6 text-red-500" />
                    {language === 'ru' ? 'Видеопрезентация' : 'Video Presentation'}
                  </h3>
                  <div className="aspect-video">
                    <iframe
                      src={`${youtubeEmbedUrl}?rel=0&modestbranding=1`}
                      title={`${title} video`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                      loading="lazy"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Contact Form Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 bg-white p-8 border border-gray-200">
                <h3 className="font-heading text-2xl text-night mb-6">{t('villa.request')}</h3>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="villa-contact-form">
                  <input
                    type="text"
                    placeholder={t('contact.name')}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-minimal w-full"
                    required
                    data-testid="contact-name-input"
                  />
                  <input
                    type="email"
                    placeholder={t('contact.email')}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-minimal w-full"
                    required
                    data-testid="contact-email-input"
                  />
                  <input
                    type="tel"
                    placeholder={t('contact.phone')}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-minimal w-full"
                    data-testid="contact-phone-input"
                  />
                  <textarea
                    placeholder={t('contact.message')}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="input-minimal w-full min-h-[120px] resize-none"
                    required
                    data-testid="contact-message-input"
                  />
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full btn-primary py-4 text-sm tracking-widest uppercase"
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? '...' : t('contact.send')}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default VillaPage;
